import Paper from "@mui/material/Paper";
import axios from "axios";
import moment from "moment-timezone";

import { Chip } from "@mui/material";
import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";
import { useSignal, useSignalEffect } from "@preact/signals-react";
import React, { useState } from "react";
import { MAX_TABLE_ROWS } from "../data/constants";
import { app } from "../signals/AppSignal";
import { parsePlan } from "../utils/StringUtil";

interface ISubscriptions {
  id: number;
  dateCreated: string;
  accountId: string;
  slug: string;
  accountName: string;
  userId: string;
  userName: string;
  userEmail: string;
  eventType: string;
  eventDate: string;
  planId: string | null;
  isTrial: string | null;
  billingPeriod: string | null;
  renewalDate: string;
}

const Subscriptions = () => {
  useSignal(0);

  const columns: GridColDef[] = [
    {
      field: "id",
      type: "number",
      headerAlign: "right",
      headerName: "#",
      width: 50,
    },
    {
      field: "dateCreated",
      type: "string",
      headerAlign: "left",
      headerName: "Date Created",
      sortable: true,
      width: 200,
    },
    {
      field: "accountId",
      type: "string",
      headerAlign: "left",
      headerName: "Account Id",
      sortable: true,
      width: 100,
    },
    {
      field: "slug",
      type: "string",
      headerAlign: "left",
      headerName: " slug",
      sortable: true,
      width: 150,
    },
    {
      field: "accountName",
      type: "string",
      headerAlign: "left",
      headerName: "Account Name",
      sortable: true,
      width: 150,
    },
    {
      field: "userId",
      type: "string",
      headerAlign: "left",
      headerName: "User Id",
      sortable: true,
      width: 100,
    },
    {
      field: "userName",
      type: "string",
      headerAlign: "left",
      headerName: "User Name",
      sortable: true,
      width: 150,
    },
    {
      field: "userEmail",
      type: "string",
      headerAlign: "left",
      headerName: "Email",
      sortable: true,
      width: 200,
    },
    {
      field: "eventDate",
      type: "string",
      headerAlign: "left",
      headerName: "Event Date",
      sortable: true,
      width: 200,
    },
    {
      field: "eventType",
      type: "string",
      headerAlign: "left",
      headerName: "Event Type",
      sortable: true,
      width: 150,
    },
    {
      field: "isTrial",
      type: "boolean",
      headerAlign: "center",
      headerName: "Trial",
      sortable: true,
      width: 80,
    },
    {
      field: "planId",
      type: "string",
      headerAlign: "left",
      headerName: "Plan Id",
      sortable: true,
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={params.value === "free" ? "success" : "warning"}
          sx={{ width: "100%", justifyContent: "center" }}
        />
      ),
      cellClassName: (params) => getPlanClass(params.value),
    },
    {
      field: "billingPeriod",
      type: "string",
      headerAlign: "center",
      headerName: "Billing Type",
      sortable: true,
      width: 100,
      renderCell: (params) => (
        <div className="w-100 text-center align-center">
          {params.value || "monthly"}
        </div>
      ),
    },
    {
      field: "renewalDate",
      type: "string",
      headerAlign: "left",
      headerName: "Renewal Added",
      sortable: true,
      width: 200,
    },
  ];

  const [subscriptions, setSubscriptions] = useState<ISubscriptions[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [pagnition, setPagnation] = useState<any>({});

  const [paginationModel, setPaginationModel] = useState<{
    page: number;
    pageSize: number;
  }>({ page: 0, pageSize: 50 });

  const rowCount = subscriptions.length || 0;
  const pageCount = Math.ceil(rowCount / paginationModel.pageSize);
  const startIndex = paginationModel.page * paginationModel.pageSize + 1;
  const endIndex = Math.min(
    (paginationModel.page + 1) * paginationModel.pageSize,
    rowCount,
  );

  const getTablePage = (totalPages: number) => {
    const pages: React.ReactNode[] = [];
    const currentPage = pagnition.currentPage;

    // Controls how many pages to show before and after the current page
    const neighbors = 2;

    for (let i = 1; i <= totalPages; i++) {
      // Always show the first page, the last page, and pages immediately surrounding the current page
      if (
        i === 1 ||
        i === totalPages ||
        (i >= currentPage - neighbors && i <= currentPage + neighbors)
      ) {
        const isCurrent = currentPage === i;
        pages.push(
          <li key={i} className="page-item">
            <button
              disabled={isCurrent}
              className={
                isCurrent
                  ? "page-link text-white fw-bold active"
                  : "page-link text-dark"
              }
              onClick={() => getSubscriptionList(i)}
            >
              {i}
            </button>
          </li>,
        );
      }
      // Add Ellipsis if we are skipping chunks of pages
      else if (
        i === currentPage - neighbors - 1 ||
        i === currentPage + neighbors + 1
      ) {
        pages.push(
          <li key={`ellipsis-${i}`} className="page-item disabled">
            <span className="page-link text-muted bg-light border-0">...</span>
          </li>,
        );
      }
    }

    return pages;
  };

  const handlePageSizeChange = (event: { target: { value: string } }) => {
    setPaginationModel((prev) => ({
      ...prev,
      pageSize: parseInt(event.target.value, 10),
      page: 0, // Reset to first page if pageSize changes
    }));
  };

  const handleNext = () => {
    setPaginationModel((prev) => ({
      ...prev,
      page: Math.min(prev.page + 1, pageCount - 1),
    }));
  };

  const handlePrev = () => {
    setPaginationModel((prev) => ({
      ...prev,
      page: Math.max(prev.page - 1, 0),
    }));
  };

  const getPlanClass = (status: string) => {
    switch (status) {
      case "free":
        return "status-completed";
      case "basic":
        return "status-pending";
      case "pro":
        return "status-cancelled";
      case "ultimate":
        return "status-cancelled";
      default:
        return "";
    }
  };

  const convertUTCToLocal = (utcDateStr: string) => {
    return moment
      .utc(utcDateStr) // Parse as UTC
      .tz(moment.tz.guess())
      .format("MMMM D, YYYY, HH:mm:ss");
  };

  const getSubscriptionList = async (page: number = 1) => {
    setLoading(true);
    setSubscriptions([]);
    axios
      .get(
        `/api/v1/activities/${app.value}/subscriptions?limit=${MAX_TABLE_ROWS}&page=${page}`,
      )
      .then((response) => {
        // handle success
        console.log(response.data);
        setPagnation(response.data.pagination);
        setPaginationModel({
          page: response.data.pagination.currentPage,
          pageSize: response.data.pagination.pageSize,
        });

        let usersList: ISubscriptions[] = response.data.data || [];
        usersList = usersList.map((list, index) => ({
          ...list,
          id: index + 1,
          planId: parsePlan(app.value, list.planId),
          dateCreated: convertUTCToLocal(list.dateCreated),
          eventDate: convertUTCToLocal(list.eventDate),
          renewalDate: convertUTCToLocal(list.renewalDate),
        }));
        setSubscriptions(usersList);
      })
      .catch((error) => {
        console.log(error);
        setSubscriptions([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useSignalEffect(() => {
    getSubscriptionList();
  });

  return (
    <div className="row mt-2">
      <div className="col-12">
        <div className="card">
          <div className="card-header">
            <div className="d-lg-flex">
              <div>
                <h5 className="mb-0">Subscriptions</h5>
              </div>
              <div className="ms-auto my-auto mt-lg-0 mt-4">
                <div className="ms-auto my-auto">
                  <button
                    type="button"
                    className="btn btn-icon-only btn-rounded btn-secondary mb-0 ms-2 btn-sm d-flex align-items-center justify-content-center ms-auto"
                    data-bs-toggle="tooltip"
                    data-bs-placement="bottom"
                    title="Refresh"
                    onClick={() => getSubscriptionList()}
                  >
                    <i className="material-symbols-rounded text-md">sync</i>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="card-body">
            <div className="mb-4 row align-items-center compact-controls">
              <div className="col-auto d-flex align-items-center gap-2">
                <select
                  className="form-select form-select-sm select-list"
                  onChange={handlePageSizeChange}
                  value={paginationModel.pageSize}
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="30">30</option>
                  <option value="40">40</option>
                  <option value="50">50</option>
                </select>
                <span>entries per page</span>
              </div>

              <div className="col d-flex align-items-center gap-2">
                <button
                  className="m-0 btn btn-sm btn-outline-info"
                  onClick={handlePrev}
                  disabled={paginationModel.page === 0}
                >
                  Previous
                </button>

                <button
                  className="m-0 btn btn-sm btn-outline-info"
                  onClick={handleNext}
                  disabled={paginationModel.page >= pageCount - 1}
                >
                  Next
                </button>

                <div
                  className="d-flex align-items-center"
                  style={{ height: "100%" }}
                >
                  <span style={{ lineHeight: 1 }}>
                    Page {startIndex}–{endIndex} of {rowCount}
                  </span>
                </div>
              </div>

              <div className="col d-flex align-items-center justify-content-end">
                <nav aria-label="Table navigation">
                  <ul className="pagination pagination-sm mb-0 align-items-center">
                    {/* Previous Button */}
                    <li className="page-item">
                      <button
                        className="page-link text-dark"
                        disabled={pagnition?.currentPage === 1}
                        onClick={() => {
                          const prev = pagnition?.currentPage - 1;
                          if (prev >= 1) getSubscriptionList(prev);
                        }}
                      >
                        <span aria-hidden="true">&laquo;</span>
                      </button>
                    </li>

                    {/* Dynamic Page List */}
                    {getTablePage(pagnition?.totalPages || 0)}

                    {/* Next Button */}
                    <li className="page-item">
                      <button
                        className="page-link text-dark"
                        disabled={
                          pagnition?.currentPage === pagnition?.totalPages
                        }
                        onClick={() => {
                          const next = pagnition?.currentPage + 1;
                          if (next <= pagnition?.totalPages)
                            getSubscriptionList(next);
                        }}
                      >
                        <span aria-hidden="true">&raquo;</span>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>

            <Paper sx={{ height: "550px", width: "100%" }}>
              <DataGrid
                loading={loading}
                rows={subscriptions}
                columns={columns}
                hideFooter={true}
                slots={{ toolbar: GridToolbar }}
                initialState={{ pagination: { paginationModel } }}
                paginationModel={paginationModel}
                onPaginationModelChange={(newModel) =>
                  setPaginationModel(newModel)
                }
                pageSizeOptions={[10, 20, 30, 40, 50]}
                localeText={{ noRowsLabel: "No data available to display" }}
                disableRowSelectionOnClick
                sx={{ border: 0 }}
              />
            </Paper>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Subscriptions;
